import React from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { PAGE_TITLES } from '../nav/navConfig'
import { T } from '../pages/shared'

type Crumb = { label: string; to?: string }

function crumbsFor(pathname: string, id?: string): Crumb[] {
  if (pathname.startsWith('/vv/project/') && id) {
    return [
      { label: 'V&V Platform', to: '/vv' },
      { label: 'Verification Project', to: undefined },
      { label: id.length > 10 ? `${id.slice(0, 8)}…` : id },
    ]
  }

  const hit = PAGE_TITLES[pathname]
  if (hit) {
    const [title] = hit
    return [{ label: title }]
  }

  const seg = pathname.replace(/^\//, '').split('/').filter(Boolean)
  if (seg.length <= 1) {
    return [{ label: titleCase(seg[0] || 'Home') }]
  }

  const built: Crumb[] = []
  let acc = ''
  for (let i = 0; i < seg.length; i++) {
    acc += `/${seg[i]}`
    const t = PAGE_TITLES[acc]?.[0] ?? titleCase(seg[i])
    const isLast = i === seg.length - 1
    built.push({ label: t, to: isLast ? undefined : acc })
  }
  return built
}

function titleCase(s: string) {
  return s.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

export default function Breadcrumbs() {
  const { pathname } = useLocation()
  const { id } = useParams<{ id?: string }>()
  const list = crumbsFor(pathname, id)

  if (list.length <= 1 && pathname === '/dashboard') return null

  return (
    <nav aria-label="Breadcrumb" style={{ marginBottom: 10 }}>
      <ol
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 4,
          listStyle: 'none',
          margin: 0,
          padding: 0,
          fontSize: 12,
          color: T.textMuted,
          fontFamily: T.fontBody,
        }}
      >
        <li>
          <Link
            to="/dashboard"
            style={{
              color: T.textMuted,
              textDecoration: 'none',
              fontWeight: 500,
              textUnderlineOffset: 3,
              borderRadius: 4,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.color = T.canyon
              e.currentTarget.style.textDecoration = 'underline'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = T.textMuted
              e.currentTarget.style.textDecoration = 'none'
            }}
          >
            Home
          </Link>
        </li>
        {list.map((c, i) => (
          <React.Fragment key={`${c.label}-${i}`}>
            <li aria-hidden style={{ display: 'flex', alignItems: 'center', opacity: 0.7 }}>
              <ChevronRight size={14} />
            </li>
            <li>
              {c.to ? (
                <Link
                  to={c.to}
                  style={{
                    color: T.textSecondary,
                    textDecoration: 'none',
                    fontWeight: 600,
                    textUnderlineOffset: 3,
                    borderRadius: 4,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = T.canyon
                    e.currentTarget.style.textDecoration = 'underline'
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = T.textSecondary
                    e.currentTarget.style.textDecoration = 'none'
                  }}
                >
                  {c.label}
                </Link>
              ) : (
                <span style={{ color: T.textPrimary, fontWeight: 700 }}>{c.label}</span>
              )}
            </li>
          </React.Fragment>
        ))}
      </ol>
    </nav>
  )
}
